import { notFound } from "@tanstack/solid-router";
import { createServerFn } from "@tanstack/solid-start";
import { z } from "zod";
import { withSentrySpan } from "~/lib/sentry-span";
import { authFunctionMiddleware } from "~/server/middleware/auth";

export const GetWorksheetPageDataInputSchema = z.object({
  businessId: z.string().trim().min(1),
  worksheetId: z.string().trim().min(1),
});

/**
 * Aggregate server function for the worksheet detail page.
 *
 * Fetches worksheet detail and business detail in a single HTTP round trip
 * on client-side navigation.
 */
export const getWorksheetPageData = createServerFn({ method: "GET" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => GetWorksheetPageDataInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("worksheet.pageData", async () => {
      const [businessMod, worksheetMod] = await Promise.all([
        import("../business/business.server"),
        import("./worksheets.server"),
      ]);
      const { getBusinessDetailForUser } = businessMod;
      const { getWorksheetsForUser } = worksheetMod;

      const isNew = data.worksheetId === "new";

      const [businessDetail, worksheet] = await Promise.all([
        getBusinessDetailForUser(context.user.id, data.businessId),
        isNew
          ? Promise.resolve(null)
          : getWorksheetsForUser(
              context.user.id,
              data.businessId,
              data.worksheetId,
            ),
      ]);

      if (!worksheet && !isNew) {
        // notFound() (not a bare Response) — thrown Responses can't be
        // serialized by the server RPC layer (Seroval crash → 500) and aren't
        // rejected properly on the client. notFound() renders the route's
        // notFoundComponent with a clean 404.
        throw notFound();
      }

      return { businessDetail, worksheet } as const;
    });
  });
